import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FilePlus, FolderPlus, Plus, ChevronRight, ChevronDown, Users } from 'lucide-react';
import ContextMenu from '../ui/ContextMenu';
import Tooltip from '../ui/Tooltip';

/**
 * A section's header row: the collapse toggle with the section title, and the
 * "+" button whose menu creates a document or a folder at the section root.
 * `openedAtRef` is the section's shared "a menu just opened" timestamp.
 *
 * The menu is ui/ContextMenu in keyboard mode (card 6.3): role="menu", the
 * first row focused on open, arrows / Home / End between rows, Escape hands
 * focus back to the "+".
 */
export default function SectionHeader({ title, isTeam, collapsed, onToggle, openedAtRef, onNewDocument, onNewFolder }) {
  const { t } = useTranslation();
  // The section header's "+" menu: { x, y } of its top-left corner, or null.
  const [headerMenu, setHeaderMenu] = useState(null);

  /**
   * Open the header "+" menu anchored under the button.
   *
   * Anchored to the BUTTON's rect, not the cursor: a click can land anywhere
   * inside a 24px target, and a menu that shifts a few pixels each time reads
   * as jitter. ContextMenu clamps it to the viewport, because the sidebar can
   * sit on the RIGHT (`sidebarPosition`).
   */
  const openHeaderMenu = (e) => {
    e.stopPropagation(); // the header itself toggles collapse
    const r = e.currentTarget.getBoundingClientRect();
    openedAtRef.current = Date.now();
    setHeaderMenu({ x: r.left, y: r.bottom + 4 });
  };

  return (
    <>
      <div className="doc-section-header">
        <button type="button" className="doc-section-toggle" aria-expanded={!collapsed} onClick={onToggle}>
          {collapsed ? (
            <ChevronRight size={13} className="doc-section-chevron" aria-hidden />
          ) : (
            <ChevronDown size={13} className="doc-section-chevron" aria-hidden />
          )}
          {isTeam && <Users size={12} className="doc-section-icon" aria-hidden />}
          <span className="doc-section-title">{title}</span>
        </button>
        {/* ONE "+" with a menu, replacing the two icon buttons (owner's call).
            The pair had a readability problem specific to this header: FilePlus
            and FolderPlus are the same 13px glyph with a different interior, so
            telling them apart meant hovering for the tooltip — two controls
            that read as one smudge. A "+" costs one extra click and says what
            it does.

            Note this differs from the Collections header, where the "+" stays
            standalone next to a "…": there, creating a collection is the
            dominant action and burying it would trade a click for nothing.
            Here the two creates are used comparably often, so neither earns the
            top-level slot. */}
        <div className="doc-section-actions">
          <Tooltip content={t('docSidebar.newMenu')}>
            <button
              type="button"
              className="doc-tree-action"
              aria-label={t('docSidebar.newMenu')}
              aria-haspopup="menu"
              aria-expanded={headerMenu != null}
              onClick={openHeaderMenu}
            >
              <Plus size={13} aria-hidden />
            </button>
          </Tooltip>
        </div>
      </div>

      {headerMenu && (
        <ContextMenu
          x={headerMenu.x}
          y={headerMenu.y}
          keyboard
          label={t('docSidebar.newMenu')}
          onClose={() => setHeaderMenu(null)}
          items={[
            { label: t('docSidebar.newDocument'), icon: <FilePlus size={14} aria-hidden />, onClick: onNewDocument },
            { label: t('docSidebar.newFolder'), icon: <FolderPlus size={14} aria-hidden />, onClick: onNewFolder },
          ]}
        />
      )}
    </>
  );
}
